// NEW: src/components/question-bank-slide.jsx
//
// Renders one item from the chapter-level `questionBank`. Handles four
// question types: MCQ, short, proof, caseStudy.
//
// Per-item state (MCQ selection/submission, free-text reveal) is LIFTED
// to the parent so navigating away and back preserves answers within a
// session. Parent passes `state` + `onStateChange({patch})`.
//
// Navigation is internal (own Prev / Next-or-Finish footer). The parent
// (EnhancedLearnMode) hides the shared Next/Back bar for this slide type
// so nav doesn't double up.

import { getFontFamily } from "../constants/colors";
import { pointsForAttempt } from "../constants/mastery";

const TYPE_LABELS = {
  MCQ:       "MULTIPLE CHOICE",
  short:     "SHORT ANSWER",
  proof:     "PROOF",
  caseStudy: "CASE STUDY",
};

// Split case-study prose on (a) / (b) / (c) / (d) markers so each part
// stacks with its own line break. Preserves leading intro text (anything
// before "(a)") as the first block.
function splitCaseStudyParts(text) {
  if (!text) return [];
  const parts = String(text).split(/(?=\([a-d]\))/);
  return parts.map(p => p.trim()).filter(Boolean);
}

function AnswerCard({ text, ff, background = "#FDFBF7", heading }) {
  const lines = String(text || "").split("\n");
  return (
    <div style={{
      background,
      borderRadius: 12,
      padding: 16,
      fontFamily: ff,
      animation: "qbFadeIn 0.25s ease-out",
    }}>
      {heading && (
        <div style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#6AAAC8",
          letterSpacing: "0.08em",
          marginBottom: 8,
          fontFamily: ff,
          textTransform: "uppercase",
        }}>
          {heading}
        </div>
      )}
      {lines.map((line, i) => (
        <div key={i} style={{
          fontSize: 15,
          lineHeight: 1.6,
          color: "#222222",
          marginBottom: i < lines.length - 1 ? 8 : 0,
          whiteSpace: "pre-wrap",
        }}>
          {line}
        </div>
      ))}
      <style>{`@keyframes qbFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

function McqBody({ item, mcqSelected, mcqSubmitted, update, ff, tutorialMode }) {
  const correct = item.correct;
  const options = Array.isArray(item.options) ? item.options : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {options.map((opt, i) => {
        const selected = mcqSelected === i;
        let background = "#FFFFFF";
        let border = "1.5px solid #DDDDDD";
        let color = "#222222";
        let opacity = 1;

        if (!mcqSubmitted && selected) {
          border = "2px solid #6AAAC8";
        }
        if (mcqSubmitted) {
          if (i === correct) {
            background = "#1A7A52";
            color = "#FFFFFF";
            border = "2px solid #1A7A52";
          } else if (selected) {
            background = "#D94A4A";
            color = "#FFFFFF";
            border = "2px solid #D94A4A";
          } else {
            opacity = 0.5;
          }
        }

        return (
          <button
            key={i}
            disabled={mcqSubmitted}
            onClick={() => update({ mcqSelected: i })}
            style={{
              minHeight: 44,
              width: "100%",
              textAlign: "left",
              padding: "14px 16px",
              background,
              border,
              borderRadius: 12,
              fontSize: 15,
              fontFamily: ff,
              color,
              opacity,
              cursor: mcqSubmitted ? "default" : "pointer",
              transition: "background 0.15s ease, border 0.15s ease, opacity 0.15s ease",
              lineHeight: 1.4,
            }}>
            {opt}
          </button>
        );
      })}

      {!mcqSubmitted && (
        <button
          disabled={mcqSelected === null || mcqSelected === undefined}
          onClick={() => update({ mcqSubmitted: true })}
          style={{
            marginTop: 8,
            minHeight: 44,
            padding: "12px 18px",
            background: (mcqSelected === null || mcqSelected === undefined) ? "#E5E7EB" : "#1A7A52",
            color: (mcqSelected === null || mcqSelected === undefined) ? "#9AA0A6" : "#FFFFFF",
            border: "none",
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 600,
            fontFamily: ff,
            cursor: (mcqSelected === null || mcqSelected === undefined) ? "default" : "pointer",
            boxShadow: (mcqSelected === null || mcqSelected === undefined) ? "none" : "0 4px 14px rgba(26,122,82,0.25)",
          }}>
          Submit
        </button>
      )}

      {/* MASTERY-POINTS-DISPLAY-APPLIED: show "+N pts" badge after correct submit; nothing on wrong.
          TUTORIAL-MODE-APPLIED: hide pill in tutorial — no scoring. */}
      {mcqSubmitted && mcqSelected === correct && !tutorialMode && (
        <div style={{
          display: "inline-flex", alignSelf: "flex-start", marginTop: 8,
          alignItems: "center", gap: 6,
          background: "#1A7A52", color: "#FFFFFF",
          fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
          padding: "4px 12px", borderRadius: 999, fontFamily: ff,
        }}>
          +{pointsForAttempt(Number(item?.marks) || 1, true, 1)} pts
        </div>
      )}
      {mcqSubmitted && item.explanation && (
        <AnswerCard text={item.explanation} ff={ff} heading="Why this works" />
      )}
    </div>
  );
}

function FreeTextBody({ item, revealed, update, ff }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {!revealed && (
        <button
          onClick={() => update({ revealed: true })}
          style={{
            alignSelf: "center",
            marginTop: 16,
            minHeight: 44,
            padding: "12px 22px",
            background: "#6AAAC8",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 600,
            fontFamily: ff,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(106,170,200,0.3)",
          }}>
          Show answer
        </button>
      )}

      {revealed && item.expectedAnswer && (
        <AnswerCard text={item.expectedAnswer} ff={ff} />
      )}

      {revealed && item.explanation && (
        <AnswerCard text={item.explanation} ff={ff} heading="Why this works" />
      )}
    </div>
  );
}

export function QuestionBankSlide({
  item,
  index,
  total,
  state,
  onStateChange,
  onNext,
  onPrev,
  onComplete,
  lang,
  tutorialMode = false,
}) {
  const ff = getFontFamily(lang);
  const { mcqSelected = null, mcqSubmitted = false, revealed = false } = state || {};

  const isMCQ      = item?.type === "MCQ";
  const isFreeText = item?.type === "short" || item?.type === "proof" || item?.type === "caseStudy";
  const isLast     = index === total;
  const answered   = isMCQ ? mcqSubmitted : revealed;
  const nextLocked = !answered;

  const marks = Number(item?.marks) || 0;
  const marksLabel = `${marks} mark${marks === 1 ? "" : "s"}`;

  const update = (patch) => {
    if (typeof onStateChange === "function") {
      onStateChange({ ...(state || {}), ...patch });
    }
  };

  const caseParts = item?.type === "caseStudy" ? splitCaseStudyParts(item?.question) : null;

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      background: "#FFFFFF",
      fontFamily: ff,
      width: "100%",
      minHeight: 0,
    }}>
      {/* Header strip */}
      <div style={{
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        background: "#FFFFFF",
        borderBottom: "1px solid #EEEEEE",
        flexShrink: 0,
        boxSizing: "border-box",
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#6AAAC8", fontFamily: ff, letterSpacing: "0.02em" }}>
          Q {index}/{total}
        </div>
        <div style={{
          background: "#6AAAC8",
          color: "#FFFFFF",
          borderRadius: 12,
          padding: "4px 10px",
          fontSize: 12,
          fontWeight: 600,
          fontFamily: ff,
        }}>
          {marksLabel}
        </div>
      </div>

      {/* Scroll body */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px 20px 24px",
        WebkitOverflowScrolling: "touch",
      }}>
        {/* Type label */}
        <div style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#888888",
          letterSpacing: "0.08em",
          marginBottom: 16,
          fontFamily: ff,
        }}>
          {TYPE_LABELS[item?.type] || String(item?.type || "").toUpperCase()}
        </div>

        {/* Question — case study gets split by (a)/(b)/(c) markers */}
        {caseParts ? (
          <div style={{ marginBottom: 20 }}>
            {caseParts.map((part, i) => (
              <div key={i} style={{
                fontSize: 17,
                color: "#222222",
                lineHeight: 1.5,
                marginBottom: i === caseParts.length - 1 ? 0 : 12,
                fontFamily: ff,
                whiteSpace: "pre-wrap",
              }}>
                {part}
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            fontSize: 17,
            color: "#222222",
            lineHeight: 1.5,
            marginBottom: 20,
            fontFamily: ff,
            whiteSpace: "pre-wrap",
          }}>
            {item?.question}
          </div>
        )}

        {isMCQ && (
          <McqBody item={item} mcqSelected={mcqSelected} mcqSubmitted={mcqSubmitted} update={update} ff={ff} tutorialMode={tutorialMode} />
        )}
        {isFreeText && (
          <FreeTextBody item={item} revealed={revealed} update={update} ff={ff} />
        )}
      </div>

      {/* Footer nav */}
      <div style={{
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        borderTop: "1px solid #EEEEEE",
        background: "#FFFFFF",
        flexShrink: 0,
        boxSizing: "border-box",
      }}>
        <button
          disabled={index === 1}
          onClick={() => { if (index !== 1) onPrev?.(); }}
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: "10px 14px",
            background: "transparent",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: ff,
            color: index === 1 ? "#CCCCCC" : "#6AAAC8",
            cursor: index === 1 ? "default" : "pointer",
          }}>
          ‹ Previous
        </button>

        <button
          disabled={nextLocked}
          onClick={() => {
            if (nextLocked) return;
            if (isLast) onComplete?.();
            else onNext?.();
          }}
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: "10px 20px",
            background: nextLocked ? "#E5E7EB" : (isLast ? "#1A7A52" : "#6AAAC8"),
            color: nextLocked ? "#9AA0A6" : "#FFFFFF",
            border: "none",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: ff,
            cursor: nextLocked ? "default" : "pointer",
            boxShadow: nextLocked ? "none" : (isLast ? "0 4px 14px rgba(26,122,82,0.3)" : "0 4px 12px rgba(106,170,200,0.3)"),
          }}>
          {isLast ? "Finish ✓" : "Next ›"}
        </button>
      </div>
    </div>
  );
}

export default QuestionBankSlide;
